import {
  Avatar,
  Box,
  Button,
  Icons,
  Table,
  Tabs,
  Typography,
  getColor,
  useToast,
} from "fds";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import utilsCommon from "../../shared/utils/common";
import useCheckboxGroup from "../../shared/hooks/useCheckboxGroup";

import useAdminDeptStore from "../../redux/dispatcher/useAdminDeptStore";
import useAdminPositionRoleStore from "../../redux/dispatcher/useAdminPositionRoleStore";

import {
  getAdminInfoFromLocalStorage,
  getUserInfoFromLocalStorage,
  getWorkspaceCode,
} from "../../shared/utils/auth";
import {
  ActiveUser,
  AddDepartment,
  AddDeptParam,
  ChangeDeptNameParam,
  DeptInfo,
  DeptManagerParam,
  ExternalUser,
  User,
  UserDTO,
  UserPasswd,
} from "../../types/adminDept";
import { nanoid } from "@reduxjs/toolkit";
import { Member } from "../../types/workgroup";
import readXlsxFile from "read-excel-file";
import AddExcelGroupModal from "./AddExcelGroupModal";
import DeleteUserOrDept from "./DeleteUserOrDept";
import useWorkspaceStore from "../../redux/dispatcher/useWorkspaceStore";

import {
  transformInternalUserList,
  transformExternalUserList,
  transformActiveUserList,
  transformDeptList,
} from "./tableTransformers";
import AddUserModal from "./AddUserModal";
import DeptNameModal from "./DeptNameModal";
import MoveModal from "./MoveModal";
import DeptTreePanel from "./DeptTreePanel";
import UserInfoPanel from "./UserInfoPanel";
import { WorkspaceDTO } from "../../redux/store/workspace";

// ────────────────────────────────────────────────────────────────────────────────
// 상수
// ────────────────────────────────────────────────────────────────────────────────
const ROOT_DEPT_CODE = "ae4fc0f5fd8f4142834c1ad4e929a514";

// ────────────────────────────────────────────────────────────────────────────────
// 공통 빈 상태 UI
// ────────────────────────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <Box
    height={300}
    alignItems="center"
    width="100%"
    justifyContent="center"
    direction="column"
  >
    <img
      src={require("../../shared/images/pages/File/noPeople.png")}
      alt="thumbnail"
    />
    <Typography
      type="body2_rg"
      exactColor={getColor("COLOR_TEXT_SECONDARY")}
      textAlign="center"
    >
      {message}
    </Typography>
  </Box>
);

// ────────────────────────────────────────────────────────────────────────────────
// 내부 사용자 테이블 헤더
// ────────────────────────────────────────────────────────────────────────────────
const internalTableHeaderOCI = (t: any) => [
  {
    headerText: t("admin.dept.nameId"),
    width: "calc(100% - 600px)",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.dept.company"),
    width: "200px",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.manage.subscriptionDate"),
    width: "200px",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.dept.status"),
    width: "200px",
    order: "",
    orderAction: () => {},
  },
];
const internalTableHeader = (t: any) => [
  {
    headerText: t("admin.dept.nameId"),
    width: "calc(100% - 860px)",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.dept.dept"),
    width: "200px",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.positionRole.position"),
    width: "120px",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.positionRole.job"),
    width: "120px",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.dept.emailAddr"),
    width: "200px",
    order: "",
    orderAction: () => {},
  },
  {
    headerText: t("admin.dept.phone"),
    width: "120px",
    order: "",
    orderAction: () => {},
  },
  { headerText: "", width: "100px", order: "", orderAction: () => {} },
];

// ────────────────────────────────────────────────────────────────────────────────
// Props
// ────────────────────────────────────────────────────────────────────────────────
interface Props {
  isAdmin?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────────
// 메인 컴포넌트
// ────────────────────────────────────────────────────────────────────────────────
const AdminSetting: React.FC<Props> = ({ isAdmin }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const inputFile = useRef<any>();
  const tableRef = useRef<HTMLDivElement>(null);

  // ── 탭 / 검색 상태 ──────────────────────────────────────────────────────────
  const [leftTab, setLeftTab] = useState(
    window.eco.serviceType === "OCI" &&
      getWorkspaceCode() === "MTECOCOMMON00001"
      ? 1
      : 0
  );
  const [rightTab, setRightTab] = useState(0);
  const [userStatus, setUserStatus] = useState("ALL");
  const [tempSearchWord, setTempSearchWord] = useState("");
  const [searchWord, setSearchWord] = useState("");
  const [height, setHeight] = useState(window.innerHeight - 260);

  // ── 선택된 노드 상태 ─────────────────────────────────────────────────────────
  const [targetData, setTargetData] = useState({
    id: ROOT_DEPT_CODE,
    name: "",
    type: "dept",
    state: "view", // view | userUserUpdate | deptUserUpdate | userInsert | deptManagerUpdate
  });
  const [expandValues, setExpandValues] = useState([
    ROOT_DEPT_CODE,
  ] as string[]);

  // ── 부서/사용자 데이터 ────────────────────────────────────────────────────────
  const [deptDetail, setDeptDetail] = useState([] as DeptInfo[]);
  const [userDetail, setUserDetail] = useState([] as User[]);
  const [externalDetail, setExternalDetail] = useState([] as Member[]);
  const [internalActive, setInternalActive] = useState([] as ActiveUser[]);
  const [externalActive, setExternalActive] = useState([] as ActiveUser[]);
  const [externalInActive, setExternalInActive] = useState([] as ActiveUser[]);
  const [externalLite, setExternalLite] = useState([] as ActiveUser[]);
  const [internalSearch, setInternalSearch] = useState([] as UserDTO[]);
  const [externalSearch, setExternalSearch] = useState([] as Member[]);

  // ── 사용자 편집 상태 ─────────────────────────────────────────────────────────
  const [userInfo, setUserInfo] = useState({} as any);
  const [editUserInfo, setEditUserInfo] = useState({} as User & UserDTO);
  const [externalUserInfo, setExternalUserInfo] = useState({} as ExternalUser);
  const [editExternalUserInfo, setEditExternalUserInfo] = useState(
    {} as ExternalUser
  );
  const [changeManager, setChangeManager] = useState({} as User);

  // ── 삭제/이동 모달 상태 ──────────────────────────────────────────────────────
  const [deleteUserInfo, setDeleteUserInfo] = useState(
    [] as User[] | UserDTO[]
  );
  const [deleteDeptInfo, setDeleteDeptInfo] = useState({} as DeptInfo);
  const [moveUserInfo, setMoveUserInfo] = useState([] as User[]);
  const [moveDeptInfo, setMoveDeptInfo] = useState({} as DeptInfo);

  // ── 부서 추가/수정 모달 상태 ─────────────────────────────────────────────────
  const [updateDeptInfo, setUpdateDeptInfo] = useState({} as DeptInfo);
  const [insertDeptInfo, setInsertDeptInfo] = useState({} as DeptInfo);

  // ── 사용자 추가 모달 상태 ─────────────────────────────────────────────────────
  const [openAddUserModal, toggleOpenAddUserModal] = useState(0);
  const [addUserMail, changeAddUserMail] = useState("");
  const [error, changeError] = useState({ isError: false, errorType: "" });

  // ── 비밀번호 상태 ────────────────────────────────────────────────────────────
  const [password, setPassword] = useState({
    password1: "",
    password2: "",
    type: "RANDOM",
  });
  const [isNewPassword, setIsNewPassword] = useState(false);

  // ── 엑셀 업로드 상태 ─────────────────────────────────────────────────────────
  const [fileSelected, setFileSelected] = useState({} as any);
  const [selectedDeptList, setSelectedDeptList] = useState<AddDepartment[]>();
  const [showExcelModal, toggleExcelModal] = useState(false);

  // ── 체크박스 그룹 ────────────────────────────────────────────────────────────
  const checkboxGroupInternal = useCheckboxGroup<string>();
  const checkboxGroupExternal = useCheckboxGroup<string>();
  const checkboxGroupSearchInternal = useCheckboxGroup<string>();
  const checkboxGroupSearchExternal = useCheckboxGroup<string>();
  const checkboxGroupActiveInternal = useCheckboxGroup<string>();
  const checkboxGroupActiveExternal = useCheckboxGroup<string>();
  const checkboxGroupActiveLite = useCheckboxGroup<string>();

  // ── 버튼 클릭 카운터 (Dropdown 상태 리셋용) ──────────────────────────────────
  const [buttonClick, changeButtonClick] = useState(0);

  // ── 스토어 ────────────────────────────────────────────────────────────────────
  const {
    adminDeptStore,
    getDeptDetail,
    getDeptUsers,
    getDeptLists,
    getUserDetail,
    updateUserDept,
    updateExternalUserDept,
    addUserDept,
    getActiveInternalUserList,
    getActiveExternalUserList,
    getInActiveExternalUserList,
    getExternalLiteUserList,
    getInternalUserList,
    getExternalUserList,
    downloadDeptFile,
    updateDeptParent,
    updateDeptManager,
    deleteDept,
    deleteUser,
    addDept,
    userPasswdUpdate,
    userPasswdInit,
    changeDeptName,
  } = useAdminDeptStore();

  const { positionRoleState, getPositionList, getRoleList } =
    useAdminPositionRoleStore();

  const {
    getWorkspaceInviteAgainMembers,
    updateWorkspaceMember,
    getWorkspaceInviteMember,
    deleteMember,
    workspaceStore,
  } = useWorkspaceStore();

  // ── 파생 값 ────────────────────────────────────────────────────────────────
  const companyName = useMemo(() => {
    const result = isAdmin
      ? getAdminInfoFromLocalStorage()
      : getUserInfoFromLocalStorage();
    if (window.eco.serviceType === "OCI") return t("admin.filter.userInternal");
    return result ? JSON.parse(result).ecoCommonConfig?.companyName : "COMPANY";
  }, [isAdmin, t]);

  const loginDto = useMemo(() => {
    const loginResult = (
      isAdmin ? getAdminInfoFromLocalStorage() : getUserInfoFromLocalStorage()
    ) as string;
    return JSON.parse(loginResult);
  }, [isAdmin]);

  // ────────────────────────────────────────────────────────────────────────────
  // useEffect
  // ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.eco.serviceType !== "OCI") {
      getDeptLists();
      getPositionList();
      getRoleList();
    }
  }, []);
  const [targetTenancy, setTargetTenancy] = useState<
    WorkspaceDTO | undefined
  >();
  useEffect(() => {
    if (workspaceStore.workspaceSideList && window.eco.serviceType === "OCI")
      setTargetTenancy(
        workspaceStore?.workspaceSideList?.filter(
          (item: WorkspaceDTO) => item.tenancyCode === getWorkspaceCode()
        )[0]
      );
  }, [workspaceStore.workspaceSideList]);
  useEffect(() => {
    if (leftTab === 0) {
      selectNode({
        value: ROOT_DEPT_CODE,
        columnText: companyName,
        type: "dept",
      });
      setRightTab(0);
      setUserStatus("ALL");
      setExpandValues([ROOT_DEPT_CODE]);
      setTempSearchWord("");
      setSearchWord("");
    } else if (leftTab === 1) {
      selectNode({
        value: "EXTERNAL",
        columnText: t("admin.dept.externalUser"),
        type: "external",
      });
      setUserStatus("ALL");
      setExpandValues([ROOT_DEPT_CODE]);
      setTempSearchWord("");
      setSearchWord("");
    }
  }, [leftTab]);

  useEffect(() => {
    if (companyName !== "" && targetData.name === "") {
      setTargetData({ ...targetData, name: companyName });
    }
  }, [companyName]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setHeight(
          window.innerHeight - tableRef.current.getBoundingClientRect().top
        );
      }
    };
    const element = tableRef.current;
    const resizeObserver = new ResizeObserver(() => {
      if (element)
        setHeight(window.innerHeight - element.getBoundingClientRect().top);
    });
    if (element) resizeObserver.observe(element);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) resizeObserver.unobserve(element);
    };
  }, []);

  // ────────────────────────────────────────────────────────────────────────────
  // 뷰 상태 판별
  // ────────────────────────────────────────────────────────────────────────────
  const checkUserState = () => {
    if (
      targetData.state === "userUserUpdate" ||
      targetData.state === "deptUserUpdate"
    )
      return "UPDATE";
    if (targetData.state === "userInsert") return "INSERT";
    if (targetData.type === "user" || targetData.type === "externalUser")
      return "VIEW";
    if (targetData.type === "external") return "EXTERNAL";
    if (targetData.type === "internal_search") return "INTERNAL_SEARCH";
    if (targetData.type === "external_search") return "EXTERNAL_SEARCH";
    if (targetData.type === "internal_active") return "INTERNAL_ACTIVE";
    if (targetData.type === "external_active") return "EXTERNAL_ACTIVE";
    if (targetData.type === "external_inactive") return "EXTERNAL_INACTIVE";
    if (targetData.type === "external_lite") return "EXTERNAL_AD_HOC";
    return "DEPT";
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 트리 검색
  // ────────────────────────────────────────────────────────────────────────────
  const searchTree = (value: string): void => {
    getDeptDetail(value);
    getDeptUsers(value);
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 노드 선택 (트리 클릭 시)
  // ────────────────────────────────────────────────────────────────────────────
  const selectNode = async (value: any) => {
    const TYPE_MAP: Record<string, string> = {
      EXTERNAL: "external",
      INTERNAL_ACTIVE: "internal_active",
      EXTERNAL_ACTIVE: "external_active",
      EXTERNAL_INACTIVE: "external_inactive",
      EXTERNAL_AD_HOC: "external_lite",
    };

    const resolvedType =
      TYPE_MAP[value.value] ??
      (value.type === "internal_search"
        ? "internal_search"
        : value.type === "external_search"
        ? "external_search"
        : value.type === "company" && searchWord !== "" && leftTab === 0
        ? "internal_search"
        : value.type === "company" && searchWord !== "" && leftTab === 1
        ? "external_search"
        : value.value === ROOT_DEPT_CODE
        ? "dept"
        : value.type);

    setTargetData({
      id: value.value,
      name: value.columnText,
      type: resolvedType,
      state: "view",
    });

    if (value.value === "EXTERNAL") {
      const result: any = await getExternalUserList("");
      setExternalDetail(result.payload ? (result.payload as Member[]) : []);
      setUserDetail([]);
      setDeptDetail([]);
    } else if (value.value === "INTERNAL_ACTIVE") {
      const result: any = await getActiveInternalUserList();
      setInternalActive(result.payload ? (result.payload as ActiveUser[]) : []);
    } else if (value.value === "EXTERNAL_ACTIVE") {
      const result: any = await getActiveExternalUserList();
      setExternalActive(result.payload ? (result.payload as ActiveUser[]) : []);
    } else if (value.value === "EXTERNAL_INACTIVE") {
      const result: any = await getInActiveExternalUserList();
      setExternalInActive(
        result.payload ? (result.payload as ActiveUser[]) : []
      );
    } else if (value.value === "EXTERNAL_AD_HOC") {
      const result: any = await getExternalLiteUserList();
      setExternalLite(result.payload ? (result.payload as ActiveUser[]) : []);
    } else if (value.type === "internal_search") {
      const result: any = await getInternalUserList(value.value);
      if (window.eco.serviceType !== "OCI" && result.payload?.users) {
        setInternalSearch(result.payload.users as UserDTO[]);
      } else if (window.eco.serviceType === "OCI" && result.payload) {
        setInternalSearch(result.payload as UserDTO[]);
      } else {
        setInternalSearch([]);
      }
    } else if (value.type === "external_search") {
      const result: any = await getExternalUserList(value.value);
      setExternalSearch(result.payload ? (result.payload as Member[]) : []);
    } else if (value.value === ROOT_DEPT_CODE || value.type === "dept") {
      const [userResult, deptResult]: [any, any] = await Promise.all([
        getDeptUsers(value.value),
        getDeptDetail(value.value),
      ]);
      setUserDetail(
        userResult.payload ? (userResult.payload.resp as User[]) : []
      );
      setDeptDetail(
        deptResult.payload ? (deptResult.payload.resp as DeptInfo[]) : []
      );
      setUserInfo({});
    } else if (
      [
        "user",
        "externalUser",
        "internalActiveUser",
        "externalActiveUser",
      ].includes(value.type)
    ) {
      if (value.type === "externalUser") {
        const match = externalDetail.find(
          (item: Member) => item.userId === value.value
        );
        if (match && [1, 3].includes(match.status)) {
          setExternalUserInfo({
            deptName: match.deptName,
            etc: match.etc,
            phoneNumber: match.phoneNumber,
            status: match.status,
            userId: match.userId,
            userName: match.userName,
            userType: match.userType,
          } as ExternalUser);
        } else {
          setExternalUserInfo({ userId: value.value } as ExternalUser);
        }
      } else {
        if (window.eco.serviceType === "OCI") {
          setUserInfo(
            userDetail?.find((item: any) => item?.userId === value.value)
          );
        } else {
          const resp: any = await getUserDetail(value.value);
          setUserInfo(resp.payload ? (resp.payload as User) : {});
        }
      }
      if (window.eco.serviceType !== "OCI") {
        setUserDetail([]);
        setDeptDetail([]);
      }
    }

    // 체크박스 및 편집 상태 초기화
    setRightTab(0);
    [
      checkboxGroupInternal,
      checkboxGroupExternal,
      checkboxGroupSearchInternal,
      checkboxGroupSearchExternal,
      checkboxGroupActiveInternal,
      checkboxGroupActiveExternal,
      checkboxGroupActiveLite,
    ].forEach((group) => group.handleAllClick([]));
    setEditUserInfo({} as User & UserDTO);
    setIsNewPassword(false);
    setPassword({ password1: "", password2: "", type: "RANDOM" });
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 검색어 처리
  // ────────────────────────────────────────────────────────────────────────────
  const doSearchWord = () => {
    if (tempSearchWord.trim() !== "") {
      const type = leftTab === 0 ? "internal_search" : "external_search";
      const columnText =
        leftTab === 0
          ? t("admin.dept.internalUser")
          : t("admin.dept.externalUser");
      selectNode({ value: tempSearchWord, columnText, type });
    } else {
      if (leftTab === 0) {
        if (userStatus === "ACTIVE") {
          selectNode({
            value: "INTERNAL_ACTIVE",
            columnText: t("admin.dept.internalUser"),
            type: "internal_active",
          });
        } else {
          selectNode({
            value: ROOT_DEPT_CODE,
            columnText: companyName,
            type: "company",
          });
        }
      } else {
        const externalNodeMap: Record<string, { value: string; type: string }> =
          {
            ACTIVE: { value: "EXTERNAL_ACTIVE", type: "external_active" },
            LITE: { value: "EXTERNAL_AD_HOC", type: "company" },
            INACTIVE: { value: "EXTERNAL_INACTIVE", type: "company" },
          };
        const node = externalNodeMap[userStatus] ?? {
          value: "EXTERNAL",
          type: "external",
        };
        selectNode({ ...node, columnText: t("admin.dept.externalUser") });
      }
    }
    setExpandValues([ROOT_DEPT_CODE]);
    setSearchWord(tempSearchWord.trim());
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 사용자 삭제 후처리
  // ────────────────────────────────────────────────────────────────────────────
  const deleteUserAfter = (param: { id: string }[]) => {
    toast.toastMsg(nanoid(), t("admin.common.alert.delete"), "success");
    setDeleteUserInfo([]);
    [
      checkboxGroupInternal,
      checkboxGroupExternal,
      checkboxGroupSearchInternal,
      checkboxGroupActiveInternal,
    ].forEach((g) => g.handleAllClick([]));

    const deleteIds = param.map((item) => item.id);
    setUserDetail(userDetail.filter((item) => !deleteIds.includes(item.id)));
    setInternalActive(
      internalActive.filter((item) => !deleteIds.includes(item.userId))
    );
    setExternalActive(
      externalActive.filter((item) => !deleteIds.includes(item.userId))
    );
    setExternalLite(
      externalLite.filter((item) => !deleteIds.includes(item.userId))
    );
    setInternalSearch(
      internalSearch.filter((item) => !deleteIds.includes(item.id))
    );
    setExternalSearch(
      externalSearch.filter((item) => !deleteIds.includes(item.userId))
    );

    if (searchWord !== "") {
      selectNode({
        value: searchWord,
        columnText:
          leftTab === 0
            ? t("admin.dept.internalUser")
            : t("admin.dept.externalUser"),
        type: leftTab === 0 ? "internal_search" : "external_search",
      });
      setExpandValues([ROOT_DEPT_CODE]);
      return;
    }

    if (leftTab === 0) {
      if (userStatus === "ALL") {
        const firstUser = deleteUserInfo[0] as any;
        selectNode({
          value: "dept" in firstUser ? firstUser?.dept?.code : ROOT_DEPT_CODE,
          columnText: "dept" in firstUser ? firstUser?.dept?.name : companyName,
          type: "dept",
        });
      } else {
        selectNode({
          value: "INTERNAL_ACTIVE",
          columnText: t("admin.dept.internalUser"),
          type: "company",
        });
      }
    } else {
      if (userStatus === "ALL") {
        selectNode({
          value: "EXTERNAL",
          columnText: t("admin.dept.externalUser"),
          type: "company",
        });
        setExpandValues([ROOT_DEPT_CODE]);
      } else if (userStatus === "LITE") {
        selectNode({
          value: "EXTERNAL_AD_HOC",
          columnText: t("admin.dept.externalUser"),
          type: "company",
        });
        setExpandValues([ROOT_DEPT_CODE]);
      } else if (userStatus === "INACTIVE") {
        selectNode({
          value: "EXTERNAL_INACTIVE",
          columnText: t("admin.dept.externalUser"),
          type: "company",
        });
        setExpandValues([ROOT_DEPT_CODE]);
      } else {
        selectNode({
          value: "EXTERNAL_ACTIVE",
          columnText: t("admin.dept.externalUser"),
          type: "company",
        });
        setExpandValues([ROOT_DEPT_CODE]);
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 사용자 유효성 검사 + 저장
  // ────────────────────────────────────────────────────────────────────────────
  const checkUserValid = async (
    user: User | ExternalUser,
    type: "INSERT" | "UPDATE"
  ) => {
    const userId = "id" in user ? user.id : user.userId;
    if (!userId || !utilsCommon.userIdValidation(userId)) {
      toast.toastMsg(nanoid(), t("admin.dept.userIdError"), "error");
      return;
    }
    const email = "id" in user ? user.email : user.userId;
    if (leftTab === 0 && (!email || !utilsCommon.emailValidation(email))) {
      toast.toastMsg(nanoid(), t("admin.dept.emailCheck"), "error");
      return;
    }
    if (
      user.phoneNumber &&
      !utilsCommon.phoneNumberValidation(user.phoneNumber)
    ) {
      toast.toastMsg(nanoid(), t("admin.dept.phoneCheck"), "error");
      return;
    }

    if (type === "UPDATE" && leftTab === 0) {
      const result: any = await updateUserDept({
        id: editUserInfo.id,
        name: editUserInfo.name,
        email: editUserInfo.email,
        phoneNumber: editUserInfo.phoneNumber,
        position: editUserInfo.position,
        role: editUserInfo.role,
        depts: {
          primary: editUserInfo?.depts?.primary ?? editUserInfo.dept,
          others: [],
        },
      } as UserDTO);
      if (result.payload) {
        toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
        setUserInfo(editUserInfo);
        setTargetData({
          id: editUserInfo.id,
          name: editUserInfo.name,
          type: "user",
          state: "view",
        });
      } else if (result.error.message) {
        toast.toastMsg(nanoid(), result.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }
    }

    if (type === "UPDATE" && leftTab === 1) {
      const result: any = await updateExternalUserDept(editExternalUserInfo);
      if (result.payload) {
        toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
        setExternalUserInfo(editExternalUserInfo);
        setTargetData({
          ...targetData,
          type:
            targetData.state === "userUserUpdate" ||
            targetData.state === "deptUserUpdate"
              ? "externalUser"
              : "dept",
          state: "view",
        });
      } else if (result.error.message) {
        toast.toastMsg(nanoid(), result.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }
    }

    if (type === "INSERT") {
      if (password.type !== "RANDOM") {
        if (password.password1 !== password.password2) {
          toast.toastMsg(nanoid(), t("admin.dept.passwordCheck"), "error");
          return;
        } else if (
          !utilsCommon.passwordValidation(
            password.password1,
            loginDto.ecoCommonConfig.passwordPolicyDTO?.minLength ?? 6,
            loginDto.ecoCommonConfig.passwordPolicyDTO?.maxLength ?? 30
          )
        ) {
          const min =
            loginDto.ecoCommonConfig.passwordPolicyDTO?.minLength ?? 6;
          const max =
            loginDto.ecoCommonConfig.passwordPolicyDTO?.maxLength ?? 30;
          toast.toastMsg(
            nanoid(),
            t("admin.dept.passwordValidation", { min, max }),
            "error"
          );
          return;
        }
      }
      const result: any = await addUserDept({
        id: editUserInfo.id,
        name: editUserInfo.name,
        email: editUserInfo.email,
        phoneNumber: editUserInfo.phoneNumber,
        position: editUserInfo.position,
        role: editUserInfo.role,
        depts: { primary: editUserInfo.dept, others: [] },
        password: password.type === "RANDOM" ? "RANDOM" : password.password1,
      } as UserDTO);
      if (result.payload) {
        toast.toastMsg(nanoid(), t("admin.common.alert.add"), "success");
        const userResult: any = await getDeptUsers(editUserInfo.dept.code);
        setUserDetail(
          userResult.payload
            ? (userResult.payload.resp as User[])
            : userDetail.concat(editUserInfo)
        );
        selectNode({
          value: editUserInfo.dept.code,
          columnText: editUserInfo.dept.name,
          type: "dept",
        });
        setEditUserInfo({} as User & UserDTO);
      } else if (result.error.message) {
        toast.toastMsg(nanoid(), result.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }
    }

    setPassword({ password1: "", password2: "", type: "RANDOM" });
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 비밀번호 변경
  // ────────────────────────────────────────────────────────────────────────────
  const changePassword = async () => {
    const viewState = checkUserState();
    const userId =
      leftTab === 0
        ? viewState === "VIEW"
          ? userInfo?.id ?? userInfo?.userId
          : editUserInfo.id
        : viewState === "VIEW"
        ? externalUserInfo.userId
        : editExternalUserInfo.userId;

    const resetState = () => {
      setIsNewPassword(false);
      setPassword({ password1: "", password2: "", type: "RANDOM" });
    };

    if (password.type === "") {
      if (password.password1 !== password.password2) {
        toast.toastMsg(nanoid(), t("admin.dept.passwordCheck"), "error");
        return;
      } else if (
        !utilsCommon.passwordValidation(
          password.password1,
          loginDto.ecoCommonConfig.passwordPolicyDTO?.minLength ?? 6,
          loginDto.ecoCommonConfig.passwordPolicyDTO?.maxLength ?? 30
        )
      ) {
        const min = loginDto.ecoCommonConfig.passwordPolicyDTO?.minLength ?? 6;
        const max = loginDto.ecoCommonConfig.passwordPolicyDTO?.maxLength ?? 30;

        toast.toastMsg(
          nanoid(),
          t("admin.dept.passwordValidation", { min, max }),
          "error"
        );
        return;
      }
      const result: any = await userPasswdUpdate({
        userId,
        password: password.password1,
      } as UserPasswd);
      if (result.payload) {
        toast.toastMsg(
          nanoid(),
          t("admin.dept.changePasswordComplete"),
          "success"
        );
        resetState();
      } else if (result.error.message) {
        toast.toastMsg(nanoid(), result.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }
    } else if (password.type === "RANDOM") {
      const result: any = await userPasswdInit(userId);
      if (result.payload) {
        toast.toastMsg(nanoid(), t("admin.dept.sendEmailMessage"), "success");
        resetState();
      } else if (result.error.message) {
        toast.toastMsg(nanoid(), result.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 엑셀 업로드
  // ────────────────────────────────────────────────────────────────────────────
  const handleChooseFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    setFileSelected(file);
    if (file.size === 0) {
      alert(t("admin.common.alert.noData"));
    } else {
      readXlsxFile(file, { sheet: 1 })
        .then((rows: any) => {
          const tempUser: AddDepartment[] = rows.slice(1).map((row: any) => ({
            id: row[0]?.toString(),
            name: row[1]?.toString(),
            email: row[2]?.toString(),
            dept: { code: row[3]?.toString(), name: row[4]?.toString() },
            position: { code: row[5]?.toString(), name: row[6]?.toString() },
            role: { name: "General" },
            phoneNumber: row[7]?.toString(),
          }));
          setSelectedDeptList(tempUser);
          toggleExcelModal(true);
        })
        .catch((err: any) => alert(err));
    }
    e.target.value = "";
  };

  // ────────────────────────────────────────────────────────────────────────────
  // OCI: 사용자 유형 전환 (내부 ↔ 외부)
  // ────────────────────────────────────────────────────────────────────────────
  const handleRenderUpdateStatus = async (info: any) => {
    const resp: any = await updateWorkspaceMember({
      ...info,
      dsdCode: getWorkspaceCode(),
      userType: info.userType === 1 ? 2 : 1,
    });
    if (info.userType === 1) {
      selectNode({
        value: ROOT_DEPT_CODE,
        columnText: companyName,
        type: "dept",
      });
    } else {
      selectNode({
        value: "EXTERNAL",
        columnText: t("admin.dept.externalUser"),
        type: "external",
      });
    }
    if (resp.payload) {
      toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
    } else if (resp.error.message === "NOT_REGISTER_MEMBER") {
      toast.toastMsg(nanoid(), t("login.findPW.notFoundUser"), "error");
    } else if (resp.error.message === "OVER_USER_LIMIT") {
      toast.toastMsg(nanoid(), t("login.findPW.overLicense"), "error");
    } else if (resp.error.message === "NOT_ALLOW_MOVE_EXTERNAL_USER") {
      toast.toastMsg(nanoid(), t("login.findPW.isWorkgroupManager"), "error");
    } else if (resp.error.message) {
      toast.toastMsg(nanoid(), resp.error.message, "error");
    } else {
      toast.toastMsg(nanoid(), t("common.alert.fail"), "error");
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // OCI: 재초대
  // ────────────────────────────────────────────────────────────────────────────
  const reInviteMember = (userId: string): void => {
    const resp: any = getWorkspaceInviteAgainMembers({
      userId,
      dsdCode: getWorkspaceCode() ?? "",
    });
    if (resp) {
      toast.toastMsg(nanoid(), t("admin.manage.reInviteMail"), "success");
    } else {
      toast.toastMsg(
        nanoid(),
        resp.error.message ?? t("admin.manage.failReInviteMail"),
        "error"
      );
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // OCI: 사용자 초대
  // ────────────────────────────────────────────────────────────────────────────
  const handleAddNewUser = async (): Promise<void> => {
    if (!utilsCommon.emailValidation(addUserMail)) {
      changeError({ isError: true, errorType: "INVALID_EMAIL_ADDRESS" });
      return;
    }
    const resp: any = await getWorkspaceInviteMember({
      dsdCode: getWorkspaceCode() ?? "",
      email: addUserMail.trim(),
      userType: openAddUserModal,
    });
    if (resp.payload) {
      toast.toastMsg(nanoid(), t("admin.manage.successAdd"), "success");
      if (openAddUserModal === 1) {
        selectNode({
          value: ROOT_DEPT_CODE,
          columnText: companyName,
          type: "dept",
        });
      } else {
        selectNode({
          value: "EXTERNAL",
          columnText: t("admin.dept.externalUser"),
          type: "external",
        });
      }
    } else {
      toast.toastMsg(
        nanoid(),
        resp.error.message === "DUPLICATE_MEMBER"
          ? t("login.findPW.duplicateEmail")
          : resp.error.message === "OVER_USER_LIMIT"
          ? t("login.findPW.overLicense")
          : resp.error.message ?? t("admin.manage.failAdd"),
        "error"
      );
    }
    toggleOpenAddUserModal(0);
    changeError({ isError: false, errorType: "" });
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 이동 모달 핸들러
  // ────────────────────────────────────────────────────────────────────────────
  const handleMoveApply = async (targetCode: string, targetName: string) => {
    if (moveUserInfo.length > 0) {
      const beforeDept = moveUserInfo[0].dept.code;
      const params: UserDTO[] = moveUserInfo.map((user: any) => ({
        id: user.id ?? user?.userId,
        name: user?.name ?? user?.userName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        position: { code: user.position.code, name: user?.position?.name },
        role: { code: user?.role.code, name: user?.role?.name },
        depts: { primary: { code: targetCode, name: targetName }, others: [] },
      }));

      let successCount = 0;
      for (const param of params) {
        const result: any = await updateUserDept(param);
        if (result.payload) {
          successCount++;
        } else {
          const msg = result.error.message || t("admin.common.alert.fail");
          toast.toastMsg(nanoid(), msg, "error");
          break;
        }
      }

      if (successCount === params.length) {
        toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }

      selectNode({ value: targetCode, columnText: targetName, type: "dept" });
      getDeptUsers(targetCode);
      getDeptUsers(beforeDept);
      setMoveUserInfo([]);
      setMoveDeptInfo({} as DeptInfo);
    } else if (moveDeptInfo?.code) {
      const result: any = await updateDeptParent({
        code: moveDeptInfo.code,
        parentCode: targetCode,
      });
      if (result.payload) {
        toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
      } else if (result.error.message) {
        toast.toastMsg(nanoid(), result.error.message, "error");
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
      }
      setMoveUserInfo([]);
      setMoveDeptInfo({} as DeptInfo);
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 부서명 변경 핸들러
  // ────────────────────────────────────────────────────────────────────────────
  const handleChangeDeptName = async (newName: string) => {
    const result: any = await changeDeptName({
      code: updateDeptInfo?.code,
      name: newName,
    } as ChangeDeptNameParam);
    if (result.payload) {
      toast.toastMsg(nanoid(), t("admin.common.alert.modify"), "success");
      setTargetData({ ...targetData, name: newName });
      setUpdateDeptInfo({} as DeptInfo);
    } else if (result.error.message) {
      toast.toastMsg(nanoid(), result.error.message, "error");
    } else {
      toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 하위 부서 추가 핸들러
  // ────────────────────────────────────────────────────────────────────────────
  const handleAddDept = async (name: string) => {
    const result: any = await addDept({
      managerId: "",
      name,
      parentCode: insertDeptInfo.code,
    } as AddDeptParam);
    if (result.payload) {
      toast.toastMsg(nanoid(), t("admin.common.alert.add"), "success");
      selectNode({
        value: insertDeptInfo.code,
        columnText: insertDeptInfo.name,
        type: "dept",
      });
      setInsertDeptInfo({} as DeptInfo);
    } else if (result.error.message) {
      toast.toastMsg(nanoid(), result.error.message, "error");
    } else {
      toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
    }
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 변환 함수 옵션 (tableTransformers 에 전달)
  // ────────────────────────────────────────────────────────────────────────────
  const internalTransformOptions = {
    targetData,
    setChangeManager,
    setEditUserInfo,
    setTargetData,
    t,
  };
  const externalTransformOptions = {
    setEditExternalUserInfo,
    setTargetData,
    reInviteMember,
    t,
  };

  // ────────────────────────────────────────────────────────────────────────────
  // 현재 뷰 상태
  // ────────────────────────────────────────────────────────────────────────────
  const viewState = checkUserState();

  // ────────────────────────────────────────────────────────────────────────────
  // 우측 패널: 제목 & 액션 버튼
  // ────────────────────────────────────────────────────────────────────────────
  const renderContentHeader = () => (
    <Box classes="adminDept-body-contents-title" justifyContent="space-between">
      <Box alignItems="center">
        <Typography>{targetData.name}</Typography>
        {targetData.type === "dept" && targetData.id !== ROOT_DEPT_CODE && (
          <Button
            btnStyle="icon"
            leadingIcon={<Icons variant="line" label="edit2" />}
            classes="mg ml-8"
            onClick={() =>
              setUpdateDeptInfo({
                code: targetData.id,
                name: targetData.name,
                tempName: targetData.name,
              } as DeptInfo)
            }
          />
        )}
      </Box>

      {/* 부서 뷰 액션 */}
      {targetData.type === "dept" &&
        window.eco.serviceType !== "OCI" &&
        !editUserInfo.dept &&
        targetData.state !== "deptManagerUpdate" && (
          <Box>
            <Button
              label={t("admin.common.button.move")}
              size="sm"
              btnStyle="fill"
              type="secondary"
              state={targetData.id === ROOT_DEPT_CODE ? "disable" : "default"}
              onClick={() => {
                setMoveDeptInfo({
                  code: targetData.id,
                  name: targetData.name,
                } as DeptInfo);
              }}
            />
            <Button
              label={t("admin.common.button.delete")}
              size="sm"
              btnStyle="fill"
              type="secondary"
              state={
                targetData.id === ROOT_DEPT_CODE ||
                userDetail?.length > 0 ||
                deptDetail?.length > 0
                  ? "disable"
                  : "default"
              }
              onClick={() =>
                setDeleteDeptInfo({
                  code: targetData.id,
                  name: targetData.name,
                } as DeptInfo)
              }
            />
            <Button
              label={t("admin.common.button.add")}
              size="sm"
              btnStyle="fill"
              type="primary"
              onClick={() => {
                if (window.eco.serviceType === "OCI") {
                  toggleOpenAddUserModal(2);
                } else {
                  setInsertDeptInfo({
                    code: targetData.id,
                    name: targetData.name,
                  } as DeptInfo);
                }
              }}
            />
            <input
              style={{ display: "none" }}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              id="excelFile"
              name="excelFile"
              type="file"
              ref={inputFile}
              multiple={false}
              onChange={handleChooseFile}
            />
            <label htmlFor="excelFile">
              <Button
                label={t("admin.dept.uploadDept")}
                size="sm"
                btnStyle="line"
              />
            </label>
            <Button
              label={t("admin.dept.downloadDept")}
              size="sm"
              btnStyle="line"
              onClick={downloadDeptFile}
            />
          </Box>
        )}

      {/* 사용자 상세 뷰 액션 */}
      {viewState === "VIEW" && (
        <Box>
          {leftTab === 0 && (
            <Button
              label={
                window.eco.serviceType !== "OCI"
                  ? t("admin.common.button.move")
                  : t("admin.manage.goExternalUser")
              }
              size="sm"
              btnStyle="fill"
              type="secondary"
              onClick={() => {
                if (window.eco.serviceType === "OCI") {
                  handleRenderUpdateStatus(userInfo);
                } else {
                  setMoveUserInfo([userInfo]);
                }
              }}
            />
          )}
          {leftTab === 1 && window.eco.serviceType === "OCI" && (
            <Button
              label={t("admin.manage.goInternalUser")}
              size="sm"
              btnStyle="fill"
              type="secondary"
              onClick={() => {
                handleRenderUpdateStatus(externalUserInfo);
                setExternalUserInfo({} as ExternalUser);
              }}
            />
          )}
          <Button
            label={t("admin.common.button.delete")}
            size="sm"
            btnStyle="fill"
            type="secondary"
            onClick={() => {
              if (leftTab === 0) {
                setDeleteUserInfo([userInfo]);
              } else {
                setDeleteUserInfo([{ id: externalUserInfo.userId } as User]);
              }
            }}
          />
          {window.eco.serviceType !== "OCI" && (
            <Button
              label={t("admin.common.button.modify")}
              size="sm"
              btnStyle="fill"
              type="primary"
              onClick={() => {
                if (leftTab === 0) setEditUserInfo(userInfo);
                else setEditExternalUserInfo(externalUserInfo);
                setTargetData({ ...targetData, state: "userUserUpdate" });
              }}
              state={
                leftTab === 0
                  ? "default"
                  : externalDetail.find(
                      (item: Member) => item.userId === externalUserInfo.userId
                    ) &&
                    [1, 3].includes(
                      externalDetail.find(
                        (item: Member) =>
                          item.userId === externalUserInfo.userId
                      )!.status
                    ) &&
                    externalUserInfo?.userType !== 3
                  ? "default"
                  : "disable"
              }
            />
          )}
        </Box>
      )}

      {/* 편집/등록 모드 액션 */}
      {["UPDATE", "INSERT"].includes(viewState) && (
        <Box>
          <Button
            label={t("admin.common.button.cancel")}
            size="sm"
            btnStyle="fill"
            type="secondary"
            onClick={() => {
              setEditUserInfo({} as User & UserDTO);
              if (targetData.state === "userUserUpdate") {
                setTargetData({
                  ...targetData,
                  type: leftTab === 0 ? "user" : "externalUser",
                  state: "view",
                });
              } else {
                const nextType =
                  searchWord !== ""
                    ? leftTab === 0
                      ? "internal_search"
                      : "external_search"
                    : leftTab === 0
                    ? "dept"
                    : "external";
                setTargetData({ ...targetData, type: nextType, state: "view" });
              }
            }}
          />
          <Button
            label={t("admin.common.button.apply")}
            size="sm"
            btnStyle="fill"
            type="primary"
            state={
              (
                "id" in editUserInfo
                  ? !!editUserInfo.id &&
                    !!editUserInfo.name &&
                    !!editUserInfo.position?.code &&
                    !!editUserInfo.role?.code &&
                    !!editUserInfo.email
                  : !!editExternalUserInfo.userId &&
                    !!editExternalUserInfo.userName
              )
                ? "default"
                : "disable"
            }
            onClick={() => {
              checkUserValid(
                leftTab === 0 ? editUserInfo : editExternalUserInfo,
                viewState as "UPDATE" | "INSERT"
              );
            }}
          />
        </Box>
      )}
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // 우측 패널: 부서장 섹션 (DEPT 뷰)
  // ────────────────────────────────────────────────────────────────────────────
  const getDeptManagerName = () => {
    if (!userDetail || userDetail.length === 0) return "";
    const manager = userDetail.find(
      (item: any) => item.id === userDetail[0].dept?.managerId
    );
    return manager?.name ?? "";
  };

  // ────────────────────────────────────────────────────────────────────────────
  // render
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <Box classes="adminDept" direction="column">
      {/* ── 모달들 ── */}
      {openAddUserModal > 0 && (
        <AddUserModal
          openAddUserModal={openAddUserModal}
          addUserMail={addUserMail}
          error={error}
          onClose={() => {
            changeAddUserMail("");
            changeError({ isError: false, errorType: "" });
            toggleOpenAddUserModal(0);
          }}
          onChangeEmail={changeAddUserMail}
          onAdd={handleAddNewUser}
        />
      )}

      {(updateDeptInfo?.code || insertDeptInfo?.code) && (
        <DeptNameModal
          mode={updateDeptInfo?.code ? "update" : "insert"}
          currentDeptName={
            updateDeptInfo?.code ? updateDeptInfo.name : insertDeptInfo.name
          }
          initialInputValue={
            updateDeptInfo?.code ? updateDeptInfo.tempName ?? "" : ""
          }
          onClose={() => {
            setInsertDeptInfo({} as DeptInfo);
            setUpdateDeptInfo({} as DeptInfo);
          }}
          onSave={updateDeptInfo?.code ? handleChangeDeptName : handleAddDept}
        />
      )}

      {showExcelModal && (
        <AddExcelGroupModal
          showExcelModal={showExcelModal}
          toggleExcelModal={toggleExcelModal}
          selectedDeptList={selectedDeptList}
          deptState={adminDeptStore.deptList}
          positionState={positionRoleState.positionList}
          fileSelected={fileSelected}
        />
      )}

      {(deleteUserInfo.length > 0 || deleteDeptInfo?.code) && (
        <DeleteUserOrDept
          onClose={() => {
            setDeleteUserInfo([]);
            setDeleteDeptInfo({} as DeptInfo);
          }}
          onDelete={async () => {
            if (deleteUserInfo.length > 0) {
              const param = (deleteUserInfo as any[]).map((user) => ({
                id: user.id ?? user.userId,
              }));
              const result: any =
                window.eco.serviceType === "OCI"
                  ? await deleteMember(
                      param.map((item) => item.id),
                      getWorkspaceCode() ?? ""
                    )
                  : await deleteUser(param);
              if (result.payload) {
                deleteUserAfter(param);
              } else if (result.error.message) {
                toast.toastMsg(nanoid(), result.error.message, "error");
              } else {
                toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
              }
            } else if (deleteDeptInfo?.code) {
              const result: any = await deleteDept(deleteDeptInfo?.code);
              if (result.payload) {
                toast.toastMsg(
                  nanoid(),
                  t("admin.common.alert.delete"),
                  "success"
                );
                selectNode({
                  value: ROOT_DEPT_CODE,
                  columnText: companyName,
                  type: "company",
                });
                setDeleteDeptInfo({} as DeptInfo);
              } else if (result.error.message) {
                toast.toastMsg(nanoid(), result.error.message, "error");
              } else {
                toast.toastMsg(nanoid(), t("admin.common.alert.fail"), "error");
              }
            }
          }}
          deleteUserInfo={deleteUserInfo}
          deleteDeptInfo={deleteDeptInfo}
        />
      )}

      {(moveUserInfo.length > 0 || moveDeptInfo?.code) && (
        <MoveModal
          moveUserInfo={moveUserInfo}
          moveDeptInfo={moveDeptInfo}
          internalTreeSubTree={[...adminDeptStore.internalTree.subTree]}
          companyName={companyName}
          ROOT_DEPT_CODE={ROOT_DEPT_CODE}
          targetId={targetData.id}
          onClose={() => {
            setMoveUserInfo([]);
            setMoveDeptInfo({} as DeptInfo);
          }}
          onApply={handleMoveApply}
          searchTree={searchTree}
        />
      )}

      {/* ── 페이지 타이틀 ── */}
      <Box classes="adminDept-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.dept")}</Typography>
      </Box>

      {/* ── 본문: 좌측 트리 + 우측 콘텐츠 ── */}
      <Box direction="row" classes="adminDept-body">
        {/* 좌측 트리 패널 */}
        <DeptTreePanel
          leftTab={leftTab}
          setLeftTab={setLeftTab}
          userStatus={userStatus}
          setUserStatus={setUserStatus}
          searchWord={searchWord}
          tempSearchWord={tempSearchWord}
          setTempSearchWord={setTempSearchWord}
          expandValues={expandValues}
          setExpandValues={setExpandValues}
          targetData={targetData}
          adminDeptStore={adminDeptStore}
          liteExternalLicenseCount={
            (isAdmin
              ? loginDto?.ecoSystemLicenseStats?.liteExternalLicenseCount
              : targetTenancy?.maxInternalUserCount) ?? 0
          }
          companyName={companyName}
          ROOT_DEPT_CODE={ROOT_DEPT_CODE}
          buttonClick={buttonClick}
          changeButtonClick={changeButtonClick}
          selectNode={selectNode}
          doSearchWord={doSearchWord}
          searchTree={searchTree}
        />

        {/* 우측 콘텐츠 패널 */}
        <Box
          ref={tableRef}
          direction="column"
          classes="adminDept-body-contents"
        >
          {renderContentHeader()}

          {/* ── DEPT 뷰 ── */}
          {viewState === "DEPT" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              {/* 부서장 */}
              {window.eco.serviceType !== "OCI" && (
                <Box classes="adminDept-body-contents-body-manager">
                  <Typography exactColor={getColor("COLOR_TEXT_SECONDARY")}>
                    {t("admin.dept.headDept")}
                  </Typography>
                  {changeManager.id ? (
                    <>
                      <Avatar
                        uniqueCode={utilsCommon.getUserColor(1)}
                        name={changeManager.name}
                      />
                      <Typography>
                        {utilsCommon.getNameWithId(
                          changeManager.name,
                          changeManager.id
                        )}
                      </Typography>
                    </>
                  ) : userDetail?.length > 0 &&
                    userDetail[0]?.dept?.managerId ? (
                    <>
                      <Avatar
                        uniqueCode={utilsCommon.getUserColor(1)}
                        name={getDeptManagerName()}
                      />
                      <Typography>
                        {utilsCommon.getNameWithId(
                          getDeptManagerName(),
                          userDetail[0].dept.managerId
                        )}
                      </Typography>
                    </>
                  ) : null}

                  {targetData.state !== "deptManagerUpdate" &&
                    userDetail?.length > 0 &&
                    !(
                      userDetail.length === 1 &&
                      userDetail[0]?.id === userDetail[0]?.dept?.managerId
                    ) && (
                      <Button
                        label={t("admin.common.button.change")}
                        size="sm"
                        btnStyle="round"
                        onClick={() => {
                          setChangeManager({} as User);
                          setTargetData({
                            ...targetData,
                            state: "deptManagerUpdate",
                          });
                          setRightTab(0);
                        }}
                      />
                    )}

                  {targetData.state === "deptManagerUpdate" && (
                    <Box>
                      <Button
                        label={t("admin.common.button.cancel")}
                        size="sm"
                        btnStyle="round"
                        onClick={() => {
                          setChangeManager({} as User);
                          setTargetData({ ...targetData, state: "view" });
                        }}
                      />
                      <Button
                        label={t("admin.common.button.apply")}
                        size="sm"
                        btnStyle="fill"
                        classes="mg ml-8"
                        state={changeManager.id ? "default" : "disable"}
                        onClick={async () => {
                          const param = {
                            code: changeManager.dept.code,
                            managerId: changeManager.id,
                          } as DeptManagerParam;
                          const result: any = await updateDeptManager(param);
                          if (result.payload) {
                            toast.toastMsg(
                              nanoid(),
                              t("admin.common.alert.modify"),
                              "success"
                            );
                            selectNode({
                              value: changeManager.dept.code,
                              columnText: changeManager.dept.name,
                              type: "dept",
                            });
                            setChangeManager({} as User);
                            setTargetData({ ...targetData, state: "view" });
                          } else if (result.error.message) {
                            toast.toastMsg(
                              nanoid(),
                              result.error.message,
                              "error"
                            );
                          } else {
                            toast.toastMsg(
                              nanoid(),
                              t("admin.common.alert.fail"),
                              "error"
                            );
                          }
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* 탭: 사용자 / 부서 */}
              {window.eco.serviceType !== "OCI" && (
                <Tabs
                  tabs={[
                    {
                      color: "default",
                      isNew: false,
                      text: t("admin.dept.user"),
                    },
                    {
                      color: "default",
                      isNew: false,
                      text: t("admin.dept.dept"),
                    },
                  ]}
                  selectTab={(value) =>
                    targetData.state !== "deptManagerUpdate" &&
                    setRightTab(value)
                  }
                  selected={rightTab}
                  size="md"
                  type="full"
                  textAlign="center"
                  classes="mg mt-12"
                />
              )}

              {/* 사용자 탭 */}
              {rightTab === 0 && (
                <Box direction="column">
                  <Box
                    classes={
                      window.eco.serviceType !== "OCI"
                        ? "adminDept-body-contents-body-userList"
                        : "adminDept-body-contents-body-userList-oci"
                    }
                  >
                    <Typography
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {t(
                        window.eco.serviceType !== "OCI"
                          ? "admin.dept.memberTotal"
                          : "admin.dept.countInternalUser",
                        { count: userDetail?.length ?? 0 }
                      )}
                    </Typography>
                    {targetData.state !== "deptManagerUpdate" && (
                      <Box>
                        {window.eco.serviceType !== "OCI" && (
                          <Button
                            label={t("admin.common.button.move")}
                            size="sm"
                            btnStyle="fill"
                            type="secondary"
                            state={
                              checkboxGroupInternal.selected.length === 0
                                ? "disable"
                                : "default"
                            }
                            onClick={() =>
                              setMoveUserInfo(
                                userDetail.filter((item) =>
                                  checkboxGroupInternal.selected.includes(
                                    item.id
                                  )
                                )
                              )
                            }
                          />
                        )}
                        <Button
                          label={t("admin.common.button.delete")}
                          size="sm"
                          btnStyle="fill"
                          type="secondary"
                          state={
                            checkboxGroupInternal.selected.length === 0
                              ? "disable"
                              : "default"
                          }
                          onClick={() =>
                            setDeleteUserInfo(
                              userDetail.filter((item: any) =>
                                checkboxGroupInternal.selected.includes(
                                  item.id ?? item.userId
                                )
                              )
                            )
                          }
                        />
                        <Button
                          label={t("admin.common.button.add")}
                          size="sm"
                          btnStyle="fill"
                          type="primary"
                          onClick={() => {
                            if (window.eco.serviceType === "OCI") {
                              toggleOpenAddUserModal(1);
                            } else {
                              setEditUserInfo({
                                ...editUserInfo,
                                dept: {
                                  code: targetData.id,
                                  name: targetData.name,
                                },
                              });
                              setTargetData({
                                ...targetData,
                                type: "user",
                                state: "userInsert",
                              });
                              setPassword({
                                password1: "",
                                password2: "",
                                type: "RANDOM",
                              });
                            }
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                  <Table
                    classes="mg mt-8"
                    width="100%"
                    height={(userDetail?.length > 0 ? height - 302 : 0) + "px"}
                    header={
                      window.eco.serviceType === "OCI"
                        ? internalTableHeaderOCI(t)
                        : internalTableHeader(t)
                    }
                    data={transformInternalUserList(
                      userDetail,
                      checkboxGroupInternal,
                      internalTransformOptions
                    )}
                    totalCount={userDetail?.length ?? 0}
                    clickIds={checkboxGroupInternal.selected.map(String)}
                    checkBoxClick={
                      targetData.state === "deptManagerUpdate"
                        ? undefined
                        : () =>
                            checkboxGroupInternal.handleAllClick(
                              checkboxGroupInternal.selected.length ===
                                userDetail.length
                                ? []
                                : userDetail.map(
                                    (q: any) => (q?.id ?? q?.userId) + ""
                                  )
                            )
                    }
                    endReached={() => {}}
                    columnHeight="md"
                  />
                  {!(userDetail?.length > 0) && (
                    <EmptyState message={t("admin.dept.noMembers")} />
                  )}
                </Box>
              )}

              {/* 부서 탭 */}
              {rightTab === 1 && (
                <Box direction="column">
                  <Box classes="adminDept-body-contents-body-deptList">
                    <Typography
                      type="body2_rg"
                      exactColor={getColor("COLOR_TEXT_SECONDARY")}
                    >
                      {t("admin.dept.subDeptCount", {
                        count: deptDetail?.length ?? 0,
                      })}
                    </Typography>
                  </Box>
                  <Table
                    classes="mg mt-8"
                    width="100%"
                    height={(deptDetail?.length > 0 ? height - 302 : 0) + "px"}
                    header={[
                      {
                        headerText: "",
                        width: "40px",
                        order: "",
                        orderAction: () => {},
                      },
                      {
                        headerText: t("admin.dept.deptName"),
                        width: "calc(100% - 710px)",
                        order: "",
                        orderAction: () => {},
                      },
                      {
                        headerText: t("admin.dept.headDept"),
                        width: "300px",
                        order: "",
                        orderAction: () => {},
                      },
                      {
                        headerText: t("admin.dept.subDept"),
                        width: "200px",
                        order: "",
                        orderAction: () => {},
                      },
                      {
                        headerText: t("admin.dept.deptMember"),
                        width: "200px",
                        order: "",
                        orderAction: () => {},
                      },
                    ]}
                    data={transformDeptList(deptDetail, t)}
                    totalCount={deptDetail?.length ?? 0}
                    endReached={() => {}}
                    columnHeight="md"
                  />
                  {!(deptDetail?.length > 0) && (
                    <EmptyState message={t("admin.dept.noSubDept")} />
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* ── 내부 사용자 검색 결과 ── */}
          {viewState === "INTERNAL_SEARCH" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {t("admin.dept.countInternalUser", {
                    count: internalSearch?.length ?? 0,
                  })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupSearchInternal.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        internalSearch.filter((item: UserDTO) =>
                          checkboxGroupSearchInternal.selected.includes(item.id)
                        )
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={(internalSearch?.length > 0 ? height - 302 : 0) + "px"}
                header={
                  window.eco.serviceType === "OCI"
                    ? internalTableHeaderOCI(t)
                    : internalTableHeader(t)
                }
                data={transformInternalUserList(
                  internalSearch,
                  checkboxGroupSearchInternal,
                  internalTransformOptions
                )}
                totalCount={internalSearch?.length ?? 0}
                clickIds={checkboxGroupSearchInternal.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupSearchInternal.handleAllClick(
                    checkboxGroupSearchInternal.selected.length ===
                      internalSearch.length
                      ? []
                      : internalSearch.map((q: UserDTO) => q.id + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(internalSearch?.length > 0) && (
                <EmptyState message={t("admin.dept.noSearchResult")} />
              )}
            </Box>
          )}

          {/* ── 외부 사용자 전체 ── */}
          {viewState === "EXTERNAL" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography type="subtitle2">
                  {externalDetail.filter((u) => u.userType !== 3).length > 0 &&
                    t("admin.dept.countExternalUser", {
                      count: externalDetail.filter((u) => u.userType !== 3)
                        .length,
                    })}
                  {externalDetail.filter((u) => u.userType === 2).length > 0 &&
                    externalDetail.filter((u) => u.userType === 3).length > 0 &&
                    " | "}
                  {externalDetail.filter((u) => u.userType === 3).length > 0 &&
                    t("admin.dept.countOTPUser", {
                      count: externalDetail.filter((u) => u.userType === 3)
                        .length,
                    })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupExternal.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        externalDetail
                          .filter((item) =>
                            checkboxGroupExternal.selected.includes(item.userId)
                          )
                          .map((item) => ({ id: item.userId } as User))
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={(externalDetail?.length > 0 ? height - 186 : 0) + "px"}
                header={
                  window.eco.serviceType === "OCI"
                    ? [
                        {
                          headerText: t("admin.dept.emailAddr"),
                          width: "calc(100% - 760px)",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.name"),
                          width: "200px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.company"),
                          width: "200px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.status"),
                          width: "120px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: "",
                          width: "100px",
                          order: "",
                          orderAction: () => {},
                        },
                      ]
                    : [
                        {
                          headerText: t("admin.dept.emailAddr"),
                          width: "calc(100% - 760px)",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.name"),
                          width: "200px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.company"),
                          width: "200px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.phone"),
                          width: "120px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: t("admin.dept.status"),
                          width: "120px",
                          order: "",
                          orderAction: () => {},
                        },
                        {
                          headerText: "",
                          width: "100px",
                          order: "",
                          orderAction: () => {},
                        },
                      ]
                }
                data={transformExternalUserList(
                  externalDetail,
                  checkboxGroupExternal,
                  externalTransformOptions
                )}
                totalCount={externalDetail?.length ?? 0}
                clickIds={checkboxGroupExternal.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupExternal.handleAllClick(
                    checkboxGroupExternal.selected.length ===
                      externalDetail.length
                      ? []
                      : externalDetail.map((q: Member) => q.userId + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(externalDetail?.length > 0) && (
                <EmptyState message={t("admin.dept.noExternalUser")} />
              )}
            </Box>
          )}

          {/* ── 외부 사용자 검색 결과 ── */}
          {viewState === "EXTERNAL_SEARCH" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography type="subtitle2">
                  {t("admin.dept.countExternalUser", {
                    count: externalSearch.filter((u) => u.userType !== 3)
                      .length,
                  })}
                  {externalSearch.filter((u) => u.userType === 3).length > 0 &&
                    " | " +
                      t("admin.dept.countOTPUser", {
                        count: externalSearch.filter((u) => u.userType === 3)
                          .length,
                      })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupSearchExternal.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        externalSearch
                          .filter((item) =>
                            checkboxGroupSearchExternal.selected.includes(
                              item.userId
                            )
                          )
                          .map((item) => ({ id: item.userId } as User))
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={(externalSearch?.length > 0 ? height - 186 : 0) + "px"}
                header={[
                  {
                    headerText: t("admin.dept.emailAddr"),
                    width: "calc(100% - 760px)",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.name"),
                    width: "200px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.company"),
                    width: "200px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.phone"),
                    width: "120px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.status"),
                    width: "120px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: "",
                    width: "100px",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformExternalUserList(
                  externalSearch,
                  checkboxGroupSearchExternal,
                  externalTransformOptions
                )}
                totalCount={externalSearch?.length ?? 0}
                clickIds={checkboxGroupSearchExternal.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupSearchExternal.handleAllClick(
                    checkboxGroupSearchExternal.selected.length ===
                      externalSearch.length
                      ? []
                      : externalSearch.map((q: Member) => q.userId + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(externalSearch?.length > 0) && (
                <EmptyState message={t("admin.dept.noSearchResult")} />
              )}
            </Box>
          )}

          {/* ── 내부 활성 사용자 ── */}
          {viewState === "INTERNAL_ACTIVE" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {t("admin.dept.countInternalUser", {
                    count: internalActive?.length ?? 0,
                  })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupActiveInternal.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        internalActive
                          .filter((item) =>
                            checkboxGroupActiveInternal.selected.includes(
                              item.userId
                            )
                          )
                          .map((item) => ({ id: item.userId } as User))
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={(internalActive?.length > 0 ? height - 186 : 0) + "px"}
                header={[
                  {
                    headerText: t("admin.dept.id"),
                    width: "calc(100% - 760px)",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.name"),
                    width: "300px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.lastAccessTime"),
                    width: "300px",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformActiveUserList(
                  internalActive,
                  checkboxGroupActiveInternal,
                  userStatus,
                  leftTab,
                  t
                )}
                totalCount={internalActive?.length ?? 0}
                clickIds={checkboxGroupActiveInternal.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupActiveInternal.handleAllClick(
                    checkboxGroupActiveInternal.selected.length ===
                      internalActive.length
                      ? []
                      : internalActive.map((q: ActiveUser) => q.userId + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(internalActive?.length > 0) && (
                <EmptyState message={t("admin.dept.noLoginUser")} />
              )}
            </Box>
          )}

          {/* ── 외부 활성 사용자 ── */}
          {viewState === "EXTERNAL_ACTIVE" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography type="subtitle2">
                  {t("admin.dept.countLoginUser", {
                    count:
                      externalActive?.filter((u) => u.userType !== 3).length ??
                      0,
                  })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupActiveExternal.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        externalActive
                          .filter((item) =>
                            checkboxGroupActiveExternal.selected.includes(
                              item.userId
                            )
                          )
                          .map((item) => ({ id: item.userId } as User))
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={(externalActive?.length > 0 ? height - 186 : 0) + "px"}
                header={[
                  {
                    headerText: t("admin.dept.id"),
                    width: "calc(100% - 760px)",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.name"),
                    width: "300px",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.lastAccessTime"),
                    width: "300px",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformActiveUserList(
                  externalActive,
                  checkboxGroupActiveExternal,
                  userStatus,
                  leftTab,
                  t
                )}
                totalCount={externalActive?.length ?? 0}
                clickIds={checkboxGroupActiveExternal.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupActiveExternal.handleAllClick(
                    checkboxGroupActiveExternal.selected.length ===
                      externalActive.length
                      ? []
                      : externalActive.map((q: ActiveUser) => q.userId + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(externalActive?.length > 0) && (
                <EmptyState message={t("admin.dept.noLoginUser")} />
              )}
            </Box>
          )}

          {/* ── 외부 미가입 사용자 ── */}
          {viewState === "EXTERNAL_INACTIVE" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography type="subtitle2">
                  {t("admin.dept.countInactiveUser", {
                    count: externalInActive?.length ?? 0,
                  })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupActiveExternal.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        externalInActive
                          .filter((item) =>
                            checkboxGroupActiveExternal.selected.includes(
                              item.userId
                            )
                          )
                          .map((item) => ({ id: item.userId } as User))
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={
                  (externalInActive?.length > 0 ? height - 186 : 0) + "px"
                }
                header={[
                  {
                    headerText: t("admin.dept.id"),
                    width: "100%",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformActiveUserList(
                  externalInActive,
                  checkboxGroupActiveExternal,
                  userStatus,
                  leftTab,
                  t
                )}
                totalCount={externalInActive?.length ?? 0}
                clickIds={checkboxGroupActiveExternal.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupActiveExternal.handleAllClick(
                    checkboxGroupActiveExternal.selected.length ===
                      externalInActive.length
                      ? []
                      : externalInActive.map((q: ActiveUser) => q.userId + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(externalInActive?.length > 0) && (
                <EmptyState message={t("admin.dept.noUnregisterUser")} />
              )}
            </Box>
          )}

          {/* ── OTP (Lite) 사용자 ── */}
          {viewState === "EXTERNAL_AD_HOC" && (
            <Box direction="column" classes="adminDept-body-contents-body">
              <Box classes="adminDept-body-contents-body-externalList">
                <Typography type="subtitle2">
                  {t("admin.dept.countOTPUser", {
                    count: externalLite?.length ?? 0,
                  })}
                </Typography>
                <Box>
                  <Button
                    label={t("admin.common.button.delete")}
                    size="sm"
                    btnStyle="fill"
                    type="secondary"
                    state={
                      checkboxGroupActiveLite.selected.length === 0
                        ? "disable"
                        : "default"
                    }
                    onClick={() =>
                      setDeleteUserInfo(
                        externalLite
                          .filter((item) =>
                            checkboxGroupActiveLite.selected.includes(
                              item.userId
                            )
                          )
                          .map((item) => ({ id: item.userId } as User))
                      )
                    }
                  />
                </Box>
              </Box>
              <Table
                classes="mg mt-8"
                width="100%"
                height={(externalLite?.length > 0 ? height - 186 : 0) + "px"}
                header={[
                  {
                    headerText: t("admin.dept.emailAddr"),
                    width: "calc(100% - 400px)",
                    order: "",
                    orderAction: () => {},
                  },
                  {
                    headerText: t("admin.dept.lastVerify"),
                    width: "400px",
                    order: "",
                    orderAction: () => {},
                  },
                ]}
                data={transformActiveUserList(
                  externalLite,
                  checkboxGroupActiveLite,
                  userStatus,
                  leftTab,
                  t
                )}
                totalCount={externalLite?.length ?? 0}
                clickIds={checkboxGroupActiveLite.selected.map(String)}
                checkBoxClick={() =>
                  checkboxGroupActiveLite.handleAllClick(
                    checkboxGroupActiveLite.selected.length ===
                      externalLite.length
                      ? []
                      : externalLite.map((q: ActiveUser) => q.userId + "")
                  )
                }
                endReached={() => {}}
                columnHeight="md"
              />
              {!(externalLite?.length > 0) && (
                <EmptyState message={t("admin.dept.noOTPUser")} />
              )}
            </Box>
          )}

          {/* ── 사용자 상세 / 편집 / 등록 ── */}
          {![
            "DEPT",
            "EXTERNAL",
            "INTERNAL_SEARCH",
            "EXTERNAL_SEARCH",
            "INTERNAL_ACTIVE",
            "EXTERNAL_ACTIVE",
            "EXTERNAL_INACTIVE",
            "EXTERNAL_AD_HOC",
          ].includes(viewState) && (
            <UserInfoPanel
              viewState={viewState as "VIEW" | "UPDATE" | "INSERT"}
              nodeType={targetData.type}
              userInfo={userInfo}
              editUserInfo={editUserInfo}
              setEditUserInfo={setEditUserInfo}
              externalUserInfo={externalUserInfo}
              editExternalUserInfo={editExternalUserInfo}
              setEditExternalUserInfo={setEditExternalUserInfo}
              positionList={positionRoleState.positionList}
              roleList={positionRoleState.roleList}
              buttonClick={buttonClick}
              changeButtonClick={changeButtonClick}
              password={password}
              setPassword={setPassword}
              isNewPassword={isNewPassword}
              setIsNewPassword={setIsNewPassword}
              onChangePassword={changePassword}
              ROOT_DEPT_CODE={ROOT_DEPT_CODE}
              companyName={companyName}
              leftTab={leftTab}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminSetting;
